import { ComponentType } from 'react';
import { PlainJsonReviewTable } from './plainJsonReviewTable';
import { HudReviewTable } from './genericDocReview/attorneyHud/hudReviewTable';
import { ProofOfReceiptReviewTable } from './genericDocReview/proofOfReceiptRentalIncome/proofOfReceiptReviewTable';
import { PropertyTaxesReviewTable } from './genericDocReview/propertyTaxes/propertyTaxesReviewTable';
import { UwHudReviewTable } from './genericDocReview/uwHud/uwHudReviewTable';
import { ClosingInstructionLetterReviewTable } from './genericDocReview/closingInstructionLetter/closingInstructionLetterReviewTable';
import { ClosingProtectionLetterReviewTable } from './genericDocReview/closingProtectionLetter/closingProtectionLetterReviewTable';
import { RecordedDocumentReviewTable } from './genericDocReview/trailingDocs/recordedDocumentReviewTable';
import { FinalTitlePolicyReviewTable } from './genericDocReview/trailingDocs/finalTitlePolicyReviewTable';
import { EntityReviewTable } from './genericDocReview/borrowerEntity/entityReviewTable';
import { OwnershipReconciliationTable } from './genericDocReview/borrowerEntity/ownershipReconciliationTable';
import { EntityDetailsSection } from './genericDocReview/borrowerEntity/entityDetailsSection';
import { SellerEntityDocumentsReviewTable } from './genericDocReview/sellerEntityDocuments/sellerEntityDocumentsReviewTable';
import {
  CLOSING_INSTRUCTION_LETTER,
  CLOSING_PROTECTION_LETTER,
  EIN_LETTER,
  HOA,
  HUD,
  HUD_DOC,
  PROOF_OF_RECEIPT,
  PURCHASE_CONTRACT,
  TAX_CERTIFICATE,
  UW_HUD,
  WHOLESALER_CONTRACT,
  GC_LICENSE,
  BROKER_LICENSE,
  FINAL_TITLE_POLICY,
  RECORDED_MORTGAGE,
  RECORDED_ALR,
  ARTICLES_OF_INCORPORATION,
  BORROWING_CORP_STOCK_CERTIFICATE,
  BYLAWS,
  CERTIFICATE_OF_FORMATION,
  FILING_RECEIPT,
  OPERATING_AGREEMENT,
  CERTIFICATE_OF_GOOD_STANDING,
  CERTIFICATE_OF_GOOD_STANDING_BASE,
  CERTIFICATE_OF_EXISTENCE,
  BORROWER_LLC_CERTIFICATE_OF_GOOD_STANDING,
  FOREIGN_ENTITY_REGISTRATION,
  RECORDED_UCC1_STATE,
  RECORDED_UCC1_COUNTY,
  SELLER_ENTITY_DOCUMENTS,
  CERT_OF_FORMATION_OR_INC,
  CERT_OF_GOOD_STANDING_OR_EXISTENCE,
  OPERATING_AGREEMENT_BYLAWS_STOCK,
  RECORDED_CEMA,
  RECORDED_BUILDING_MORTGAGE,
  BORROWER_IDENTITY_VERIFICATION,
  VERIFICATION_OF_MORTGAGE,
} from '@roc/feature-documents';
import { HoaReviewTable } from './genericDocReview/hoa/hoaReviewTable';
import { PurchaseContractReviewTable } from './genericDocReview/purchaseContract/purchaseContractReviewTable';
import { VerificationOfMortgageReviewTable } from './genericDocReview/verificationOfMortgage/verificationOfMortgageReviewTable';
import { WholesalerContractReviewTable } from './genericDocReview/wholesalerContract/wholesalerContractReviewTable';
import { IdentityReviewTable, IdentityFinancialDataSection } from './genericDocReview/identityDocument/identityReviewTable';
import { IdentityUploader } from './genericDocReview/identityDocument/identityUploader';
import { DefaultDocReviewUploader } from './docReviewUploader';
import { HudFundingTemplateSection } from './genericDocReview/attorneyHud/hudFundingTemplateSection';

interface DocReviewLayout {
  title?: string;
  frameless?: boolean;
  // rendered above the section's own title row
  preSection?: ComponentType<{ data: Record<string, any> }>;
  // rendered below the section's frame
  postSection?: ComponentType<{ data: Record<string, any> }>;
}

const OWNERSHIP_LAYOUT: DocReviewLayout = {
  title: 'Ownership Reconciliation',
  frameless: true,
  preSection: EntityDetailsSection,
};

// resolved per call, not at module load: feature-documents imports back from this library, so a
// constant read while this module is initialising can still be undefined
const layoutFor = (documentName: string): DocReviewLayout =>
  ({
    [OPERATING_AGREEMENT]: OWNERSHIP_LAYOUT,
    [OPERATING_AGREEMENT_BYLAWS_STOCK]: OWNERSHIP_LAYOUT,
    [VERIFICATION_OF_MORTGAGE]: { title: 'Mortgage Data' },
    [HUD]: { postSection: HudFundingTemplateSection },
    [BORROWER_IDENTITY_VERIFICATION]: {
      postSection: IdentityFinancialDataSection,
    },
  }[documentName] ?? {});

export const docReviewSectionLayout = (
  documentName: string
): { title?: string; frameless?: boolean } => {
  const { preSection, postSection, ...sectionProps } = layoutFor(
    documentName
  );
  return sectionProps;
};

export const DocReviewPreSection = ({
  documentName,
  data,
}: DocReviewTableResolverProps) => {
  const PreSection = layoutFor(documentName).preSection;
  return PreSection ? <PreSection data={data} /> : null;
};

// the upload section at the top of the dialog; DefaultDocReviewUploader unless the document has its own
export const DocReviewUploaderResolver = ({ documentName }: { documentName: string }) => {
  const Uploader =
    ({
      [BORROWER_IDENTITY_VERIFICATION]: IdentityUploader,
    } as Record<string, ComponentType>)[documentName] ?? DefaultDocReviewUploader;
  return <Uploader />;
};

export const DocReviewPostSection = ({
  documentName,
  data,
}: DocReviewTableResolverProps) => {
  const PostSection = layoutFor(documentName).postSection;
  return PostSection ? <PostSection data={data} /> : null;
};

export interface DocReviewTableResolverProps {
  documentName: string;
  data: Record<string, any>;
}

export const DocReviewTableResolver = ({
  documentName,
  data,
}: DocReviewTableResolverProps) => {
  const Table = {
    [EIN_LETTER]: EntityReviewTable,
    [HUD]: HudReviewTable,
    [PROOF_OF_RECEIPT]: ProofOfReceiptReviewTable,
    [TAX_CERTIFICATE]: PropertyTaxesReviewTable,
    [UW_HUD]: UwHudReviewTable,
    [HUD_DOC]: UwHudReviewTable,
    [HOA]: HoaReviewTable,
    [VERIFICATION_OF_MORTGAGE]: VerificationOfMortgageReviewTable,
    [PURCHASE_CONTRACT]: PurchaseContractReviewTable,
    [WHOLESALER_CONTRACT]: WholesalerContractReviewTable,
    [CLOSING_INSTRUCTION_LETTER]: ClosingInstructionLetterReviewTable,
    [CLOSING_PROTECTION_LETTER]: ClosingProtectionLetterReviewTable,
    [GC_LICENSE]: PlainJsonReviewTable,
    [BROKER_LICENSE]: PlainJsonReviewTable,
    [FINAL_TITLE_POLICY]: FinalTitlePolicyReviewTable,
    [RECORDED_MORTGAGE]: RecordedDocumentReviewTable,
    [RECORDED_ALR]: RecordedDocumentReviewTable,
    [CERTIFICATE_OF_FORMATION]: EntityReviewTable,
    [ARTICLES_OF_INCORPORATION]: EntityReviewTable,
    [OPERATING_AGREEMENT]: OwnershipReconciliationTable,
    [OPERATING_AGREEMENT_BYLAWS_STOCK]: OwnershipReconciliationTable,
    [CERT_OF_FORMATION_OR_INC]: EntityReviewTable,
    [CERT_OF_GOOD_STANDING_OR_EXISTENCE]: EntityReviewTable,
    [BYLAWS]: EntityReviewTable,
    [BORROWING_CORP_STOCK_CERTIFICATE]: EntityReviewTable,
    [FILING_RECEIPT]: EntityReviewTable,
    [CERTIFICATE_OF_GOOD_STANDING]: EntityReviewTable,
    [CERTIFICATE_OF_GOOD_STANDING_BASE]: EntityReviewTable,
    [CERTIFICATE_OF_EXISTENCE]: EntityReviewTable,
    [BORROWER_LLC_CERTIFICATE_OF_GOOD_STANDING]: EntityReviewTable,
    [FOREIGN_ENTITY_REGISTRATION]: EntityReviewTable,
    [RECORDED_UCC1_STATE]: RecordedDocumentReviewTable,
    [RECORDED_UCC1_COUNTY]: RecordedDocumentReviewTable,
    [SELLER_ENTITY_DOCUMENTS]: SellerEntityDocumentsReviewTable,
    [RECORDED_CEMA]: RecordedDocumentReviewTable,
    [RECORDED_BUILDING_MORTGAGE]: RecordedDocumentReviewTable,
    [BORROWER_IDENTITY_VERIFICATION]: IdentityReviewTable,
  }[documentName];
  if (!Table) return null;
  return <Table data={data} />;
};
